/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { Injectable, InjectionToken } from '@angular/core';
import {
  BasedService,
  EMPTY_STRING,
  ListItemResource,
  ListResource,
  ResourceServiceConfig,
  StateResource,
  TypedActionCreatorWithProps,
} from '@common/tech-shared';
import { Resource, ResourceUri } from '@ngxp/rest';
import { Observable } from 'rxjs';
import { CommandActions } from './+state/command.actions';
import { CommandListResourceService } from './command-list-resource.service';
import { CommandResourceService } from './command-resource.service';
import { CommandListResourceServiceConfig, CommandOrder, CommandResource, CreateCommand } from './command.model';

@Injectable()
export abstract class CommandBasedService<
  B extends Resource,
  T extends Resource,
  LB extends Resource,
  LT extends ListResource,
  LI extends ListItemResource,
  R,
> extends BasedService<B, T, LB, LT, LI, CommandResourceService<B, T>, CommandListResourceService<LB, LT, LI>> {
  constructor() {
    super();
  }

  override getConcurrentModificationAction(): TypedActionCreatorWithProps<{ effectedResourceUri: ResourceUri }> {
    return CommandActions.publishConcurrentModificationAction;
  }

  override _createResourceService(config: ResourceServiceConfig<B>): CommandResourceService<B, T> {
    return new CommandResourceService<B, T>(config);
  }

  override _createListResourceService(config: CommandListResourceServiceConfig<LB>): CommandListResourceService<LB, LT, LI> {
    return new CommandListResourceService<LB, LT, LI>(config);
  }

  public save(toSave: R, snackBarMessage: string = EMPTY_STRING): Observable<StateResource<CommandResource>> {
    return this._getResourceService().save(toSave, { snackBarMessage });
  }

  public create(toCreate: R, snackBarMessage: string = EMPTY_STRING): Observable<StateResource<CommandResource>> {
    return this._getListResourceService().createListItem(toCreate, { snackBarMessage });
  }

  public createCommandByResource(
    linkRel: string,
    command: CreateCommand,
    snackBarMessage: (resource: T) => string = () => EMPTY_STRING,
  ): Observable<StateResource<CommandResource>> {
    return this._getResourceService().createCommand(linkRel, command, snackBarMessage);
  }

  public selectCommandByResource(linkRel: string, order: CommandOrder): Observable<StateResource<CommandResource>> {
    return this._getResourceService().selectCommand(linkRel, order);
  }
}

export const COMMAND_SERVICE: InjectionToken<
  CommandBasedService<Resource, Resource, Resource, ListResource, ListItemResource, any>
> = new InjectionToken('CommandService - Token');
